import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldAlert, LogOut, Loader2 } from "lucide-react";

/**
 * ImpersonationBanner — shown across the workspace shell when a Super Admin
 * is currently viewing an organisation they are NOT a regular member of.
 * Provides a single-click exit hatch back to the global Super Admin context.
 */
export default function ImpersonationBanner() {
  const [info, setInfo] = useState(null); // { is_impersonating, organisation: {name, slug} }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const load = () => api.get("/workspace/me").then((r) => setInfo(r.data)).catch(() => setInfo(null));
    load();
    window.addEventListener("noam:org-updated", load);
    return () => window.removeEventListener("noam:org-updated", load);
  }, []);

  const stop = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/workspace/admin/stop-impersonation");
      // Reload to pick up the cleared cookie and refetch user/org context everywhere
      window.location.href = "/app/admin";
    } catch {
      setBusy(false);
    }
  };

  if (!info?.is_impersonating && !info?.organisation?.historical_twin) return null;
  const orgName = info?.organisation?.name || "—";
  const isTwin = !!info?.organisation?.historical_twin;

  return (
    <>
      {isTwin && (
        <div
          className="flex items-center gap-3 px-4 lg:px-6 py-2 bg-[#DD5A5A]/20 border-b-2 border-[#DD5A5A] text-xs"
          data-testid="historical-twin-banner"
        >
          <ShieldAlert className="w-4 h-4 text-[#FF7B7B] shrink-0" />
          <div className="flex-1 min-w-0 tracking-wide">
            <span className="font-bold text-[#FF7B7B] uppercase">Historical Twin — Not Live Organization</span>
            <span className="mx-2 opacity-50">·</span>
            <span className="text-[#FFC9C9]">Replay historique isolé ({orgName}). Aucune donnée réelle. Naviguez de T à T+1 via Org Model (date) et le Diff temporel.</span>
          </div>
        </div>
      )}
      {info?.is_impersonating && (
    <div
      className="flex items-center gap-3 px-4 lg:px-6 py-2 bg-[#EF9F27]/15 border-b border-[#EF9F27]/40 text-[#FCD9A2] text-xs"
      data-testid="impersonation-banner"
    >
      <ShieldAlert className="w-4 h-4 text-[#EF9F27] shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-semibold text-[#EF9F27]">Mode impersonation</span>
        <span className="mx-2 opacity-50">·</span>
        <span>Vous consultez l'organisation </span>
        <span className="font-semibold text-white">{orgName}</span>
        <span className="opacity-70"> en tant que Super Admin.</span>
      </div>
      <button
        onClick={stop}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md border border-[#EF9F27]/50 text-[#EF9F27] hover:bg-[#EF9F27]/20 disabled:opacity-50 transition-colors"
        data-testid="impersonation-stop-btn"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
        Quitter l'impersonation
      </button>
    </div>
      )}
    </>
  );
}
